import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { clickAsLink } from 'helpers/link_helpers';

import { saveRelatedContactsQuery } from 'components/contacts/api';
import ActionsDropdown from 'components/dropdowns/actions_dropdown';

import {
  addToGroup,
  deleteFromGroup,
  newGroup,
  startBulkDelete,
  startDeleteGroups,
  startEmailGroup,
  startEditGroups,
  startBulkTagging,
  startNewCallList
} from '../store/actions/companies';

import { startAddToCallList } from '../store/actions/call_lists';
import { retrieveCompanies } from '../store/actions/search';
import { getCallLists } from '../store/selectors/call_lists';
import { canFindDuplicates } from '../store/selectors/ui';
import { toggleView as toggleViewDuplicates } from '../store/actions/duplicates';

export class CompanyActionsDropdown extends React.Component {
  static propTypes = {
    aggregateView: PropTypes.bool.isRequired,
    callLists: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    })).isRequired,
    canAccessCrm: PropTypes.bool.isRequired,
    canDelete: PropTypes.bool.isRequired,
    canGroup: PropTypes.bool,
    canManageTags: PropTypes.bool.isRequired,
    companies: PropTypes.array.isRequired,
    currentUser: PropTypes.shape({
      callListsEnabled: PropTypes.bool
    }).isRequired,
    groups: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })).isRequired,
    retrieveCompanies: PropTypes.func.isRequired,
    selectedCompanyIds: PropTypes.array.isRequired,
    selectedGroupIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    showFindDuplicatesOption: PropTypes.bool,
    startDeleteGroups: PropTypes.func.isRequired,
    startEditGroups: PropTypes.func.isRequired,
    toggleAlert: PropTypes.func.isRequired,
    onAddLeadsToProjects: PropTypes.func,
    onAddToCallList: PropTypes.func.isRequired,
    onAddToGroup: PropTypes.func.isRequired,
    onBulkDelete: PropTypes.func.isRequired,
    onBulkTagging: PropTypes.func.isRequired,
    onCreateCallList: PropTypes.func.isRequired,
    onCreateEmailGroup: PropTypes.func.isRequired,
    onCreateGroup: PropTypes.func.isRequired,
    onDeleteFromGroup: PropTypes.func.isRequired,
    onToggleFindDuplicates: PropTypes.func.isRequired
  };

  findSelectedGroups() {
    const { groups, selectedGroupIds } = this.props;
    if (selectedGroupIds.length === 0) {
      return [];
    }

    return groups.filter(group => selectedGroupIds.includes(group.id));
  }

  getCompany(id) {
    return this.props.companies.find(company => (
      company.id === id
    ));
  }

  handleAddToCallList = (list) => {
    const { onAddToCallList, selectedCompanyIds } = this.props;
    onAddToCallList({ listId: list.id, contactIds: selectedCompanyIds });
  };

  handleAddToGroup = (group) => {
    const { selectedCompanyIds, onAddToGroup } = this.props;
    onAddToGroup(group, selectedCompanyIds);
  };

  handleDeleteFromGroup = (group) => {
    const { retrieveCompanies, selectedCompanyIds, onDeleteFromGroup } = this.props;
    onDeleteFromGroup(group, selectedCompanyIds);
    retrieveCompanies();
  };

  handleSeeRelatedContacts = (e) => {
    const { selectedCompanyIds, toggleAlert } = this.props;
    saveRelatedContactsQuery({ companyIds: selectedCompanyIds })
      .then((relatedSearch) => {
        const params = `back_to=${encodeURIComponent(window.location.href)}`;
        clickAsLink(window, `/contacts/related_contacts/${relatedSearch.id}?${params}`, e);
      })
      .catch(() => {
        toggleAlert('An error occurred when searching related contacts.', true);
      });
  }

  handleStartDeleteGroups = () => {
    this.props.startDeleteGroups(this.findSelectedGroups());
  };

  handleStartEditGroups = () => {
    this.props.startEditGroups(this.findSelectedGroups());
  }

  handleMergePeople = (e) => {
    const { selectedCompanyIds } = this.props;
    const companyOne = this.getCompany(selectedCompanyIds[0]);
    const linkUrl = `${companyOne.mergePath}?company_ids=${JSON.stringify(this.props.selectedCompanyIds)}`;
    clickAsLink(window, linkUrl, e);
  }

  handleEmailToGroup = () => {
    const { companies, selectedCompanyIds, onCreateEmailGroup } = this.props;
    const emails = companies.map(company => (
      selectedCompanyIds.indexOf(company.id) >= 0 ? company.email : null
    )).filter(Boolean);
    onCreateEmailGroup(emails);
  };

  handleFindDuplicates = () => {
    const { onToggleFindDuplicates, retrieveCompanies } = this.props;
    onToggleFindDuplicates(true);
    retrieveCompanies();
  }

  isDisabled = () => {
    const { selectedGroupIds, selectedCompanyIds, showFindDuplicatesOption } = this.props;

    if (showFindDuplicatesOption || selectedCompanyIds.length > 0) return false;
    return selectedGroupIds.length === 0 || selectedGroupIds[0] === 'ungrouped';
  }

  render() {
    const { selectedCompanyIds, showFindDuplicatesOption, currentUser } = this.props;

    const selectedGroups = this.findSelectedGroups();

    return (
      <ActionsDropdown
        {...this.props}
        callListsEnabled={currentUser && currentUser.callListsEnabled}
        canFindDuplicates={showFindDuplicatesOption}
        className="mr-4"
        disabled={this.isDisabled()}
        isCompany
        selectedGroupIds={selectedGroups.map(group => group.id)}
        selectedIds={selectedCompanyIds}
        onAddToCallList={this.handleAddToCallList}
        onAddToGroup={this.handleAddToGroup}
        onDeleteFromGroup={this.handleDeleteFromGroup}
        onDeleteGroup={this.handleStartDeleteGroups}
        onEmailToGroup={this.handleEmailToGroup}
        onFindDuplicates={this.handleFindDuplicates}
        onMergeRecords={this.handleMergePeople}
        onSeeRelatedContacts={this.handleSeeRelatedContacts}
        onUpdateGroup={this.handleStartEditGroups}
      />
    );
  }
}

const mapStateToProps = state => ({
  ...state.companies,
  ...state.search.meta.companies,
  ...state.static,
  aggregateView: state.static.aggregateView,
  callLists: getCallLists(state),
  canAccessCrm: state.ui.canAccessCrm,
  canDelete: state.ui.canDelete,
  canGroup: state.ui.canGroup,
  canManageTags: state.ui.canManageTags,
  currentUser: state.static.currentUser,
  groups: state.companies.groups,
  companies: state.search.companies,
  selectedGroupIds: state.query.companyGroupIds,
  selectedCompanyIds: state.selectList.selectedCompanyIds,
  showFindDuplicatesOption: canFindDuplicates(state)
});

const mapDispatchToProps = {
  retrieveCompanies,
  startDeleteGroups,
  startEditGroups,

  onAddToCallList: startAddToCallList,
  onAddToGroup: addToGroup,
  onBulkDelete: startBulkDelete,
  onBulkTagging: startBulkTagging,
  onCreateCallList: startNewCallList,
  onCreateEmailGroup: startEmailGroup,
  onDeleteFromGroup: deleteFromGroup,
  onCreateGroup: newGroup,
  onToggleFindDuplicates: toggleViewDuplicates
};

export default connect(mapStateToProps, mapDispatchToProps)(CompanyActionsDropdown);
